// Editable Cell Component - Inline editable cell for DetailsList

import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@fluentui/react';
import type { IColumn } from '@fluentui/react';
import type { DevPriorityTicket } from '../types';

interface EditableCellProps {
  item: DevPriorityTicket;
  column: IColumn;
  updateItem: (ticketId: number, fieldName: string, value: string | number) => void;
}

const EditableCell: React.FC<EditableCellProps> = ({ item, column, updateItem }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(
    item[column.fieldName as keyof DevPriorityTicket]?.toString() || ''
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  const handleBlur = () => {
    const currentValue = item[column.fieldName as keyof DevPriorityTicket];
    if (currentValue?.toString() !== editValue) {
      updateItem(item.ticketId, column.fieldName || '', editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(item[column.fieldName as keyof DevPriorityTicket]?.toString() || '');
      setIsEditing(false);
    }
  };

  return isEditing ? (
    <input
      ref={inputRef}
      type="text"
      value={editValue}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '4px',
        border: `1px solid ${theme.palette.themePrimary}`,
        outline: 'none',
        backgroundColor: theme.palette.white,
        color: theme.palette.neutralPrimary,
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  ) : (
    <div
      onClick={handleEdit}
      style={{
        cursor: 'pointer',
        padding: '4px',
        color: theme.palette.neutralPrimary,
      }}
    >
      {item[column.fieldName as keyof DevPriorityTicket]?.toString() || ''}
    </div>
  );
};

export default EditableCell;
